import type { ComputedRef } from 'vue'
import type { CustomerDetailsDiscipline, CustomerDetailsKid } from '@entities/customer'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'

import { getDisciplineUrl, getKidUrl } from './list/customer-links'

interface CustomerBalanceRow {
  label: string
  value: number
  tooltip: string
}

interface CustomerBalanceReturn {
  refKids: ComputedRef<CustomerDetailsKid[]>
  getKidUrl: (kidId: number) => string
  getDisciplineUrl: (disciplineId: number) => string
  getDisciplineName: (code: string) => string
  getGeneralRows: (discipline: CustomerDetailsDiscipline) => CustomerBalanceRow[]
  getExtendedRows: (discipline: CustomerDetailsDiscipline) => CustomerBalanceRow[]
}

/** Lesson balance per kid and discipline. */
export const useCustomerBalance = (): CustomerBalanceReturn => {
  const customerStore = useCustomerStore()
  const listsStore = useListsStore()

  const refKids = computed<CustomerDetailsKid[]>(() => customerStore.getDetails?.kids ?? [])

  const getDisciplineName = (code: string): string => listsStore.getLists?.discipline?.code?.[code] || code

  const getGeneralRows = (discipline: CustomerDetailsDiscipline): CustomerBalanceRow[] => [
    {
      label: 'Pace',
      value: discipline.pace || 0,
      tooltip: 'Max amount of lessons per week for this child',
    },
    {
      label: 'Unscheduled',
      value: discipline.unscheduled_bookings,
      tooltip: 'Amount of lessons that are paid and committed, but not yet confirmed or scheduled with the tutor',
    },
  ]

  const getExtendedRows = (discipline: CustomerDetailsDiscipline): CustomerBalanceRow[] => {
    const info = discipline.bookings_info

    return [
      {
        label: 'Confirmed',
        value: info.confirmed,
        tooltip: 'All scheduled and confirmed lessons for this discipline',
      },
      {
        label: 'Reserved',
        value: info.reserved,
        tooltip: 'Future lessons reserved with a tutor. These will be confirmed each month after rebill',
      },
      {
        label: 'Completed',
        value: info.completed,
        tooltip: 'Amount of lessons from last purchase with status "Completed"',
      },
      {
        label: 'Skipped',
        value: info.no_show + info.canceled + info.rescheduled,
        tooltip: `No show: ${info.no_show}. Canceled: ${info.canceled}. Rescheduled: ${info.rescheduled}`,
      },
    ]
  }

  return {
    refKids,
    getKidUrl,
    getDisciplineUrl,
    getDisciplineName,
    getGeneralRows,
    getExtendedRows,
  }
}
